// @ts-strict-ignore
/*
    Please note: all ids used correspond to user-friendly uuids, ex. org.orgId
*/

import { forIn } from 'lodash';
import { baseUrlWithQueryParams } from '../helpers';
import { IntegrationCapability, ReleaseNav, ReleaseStepType, SettingType } from '../types';
import { OrgSettingsTabType } from '../typescriptHelpers';
import { UserSettingsPageView } from '@/components/userSettings/UserSettingsComponent';
import { getSummaryViewSearchParams } from './localStorageHelper';

export enum ReleaseStepTabType {
    Summary = 'summary',
    Automations = 'automations',
    Checklist = 'checklist',
    Testers = 'testers',
}

export const STEP_RUNS_SUMMARY_TAB_PARAM = 'tab';
export const SHOW_STEP_CONFIG_SETTINGS_PARAM = 'showSettings';

export const addShowStepConfigSettingsParam = (search: string): string => {
    const params = new URLSearchParams(search);
    params.set(SHOW_STEP_CONFIG_SETTINGS_PARAM, 'true');
    return params.toString();
};

export const removeShowStepConfigSettingsParam = (search: string): string => {
    const params = new URLSearchParams(search);
    params.delete(SHOW_STEP_CONFIG_SETTINGS_PARAM);
    return params.toString();
};

export const TRIAGE_ISSUE_PARAM = 'issue';

export function createOrgRoute(orgIdParam: string): string {
    return `/dashboard/org/${orgIdParam}`;
}

export function createOrgSettingsRoute(orgIdParam: string, orgSettingsParam: OrgSettingsTabType): string {
    return `/dashboard/org/${orgIdParam}/settings/${orgSettingsParam}`;
}

export function createUserSettingsRoute(selectedTab?: UserSettingsPageView): string {
    const baseRoute = '/dashboard/user/settings';
    return selectedTab ? `${baseRoute}?selectedTab=${selectedTab}` : baseRoute;
}

export function createAppRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}`;
}

export function createAppHomeGetStartedRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/get-started`;
}

export function createAutomationsToolboxRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/automationsToolbox`;
}

export function createAutomationsToolboxIntegrationCapabilityRoute(
    orgIdParam: string,
    appIdParam: string,
    integrationCapability: IntegrationCapability,
): string {
    return `${createAutomationsToolboxRoute(orgIdParam, appIdParam)}/integrations/${integrationCapability}`;
}

export function createAppSettingsRoute(orgIdParam: string, appIdParam: string, appSettingsPath?: string): string {
    const appSettingsRoute = `/dashboard/org/${orgIdParam}/app/${appIdParam}/appSettings`;

    if (appSettingsPath) {
        return `${appSettingsRoute}${appSettingsPath}`;
    }

    return appSettingsRoute;
}

export function createAppSettingsRouteWithSettingType(
    orgIdParam: string,
    appIdParam: string,
    settingType: SettingType,
): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/appSettings/${settingType}`;
}

export const STEP_CONFIG_SETTINGS_QUERY_PARAM = 'stepConfigId';

export function createWorkflowStepSettingsRoute(orgIdParam: string, appIdParam: string, stepConfigId: string): string {
    const baseUrl = createAppSettingsRouteWithSettingType(orgIdParam, appIdParam, SettingType.WorkflowManagement);
    const params = new URLSearchParams({ [STEP_CONFIG_SETTINGS_QUERY_PARAM]: stepConfigId });
    return `${baseUrl}?${params.toString()}`;
}

export function createAppSettingsAutomationsRouteWithAutomation(orgIdParam, appIdParam, automationId: string) {
    const baseUrl = createAppSettingsRouteWithSettingType(orgIdParam, appIdParam, SettingType.Automations);
    const queryParams = {
        id: automationId,
    };

    return baseUrlWithQueryParams(baseUrl, queryParams);
}

export function createAppReleasesRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases`;
}

export function createAppBuildsRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/builds`;
}

export function createDefaultAppRoute(
    orgIdParam: string,
    appIdParam: string,
    shouldDefaultToBuildDistro?: boolean,
): { pathname: string; search?: string } {
    const pathname =
        shouldDefaultToBuildDistro ?
            createAppBuildsRoute(orgIdParam, appIdParam)
        :   createAppReleasesRoute(orgIdParam, appIdParam);
    const summarySearch = !shouldDefaultToBuildDistro ? getSummaryViewSearchParams(appIdParam) : '';

    return {
        pathname,
        ...(summarySearch ? { search: `?${summarySearch}` } : {}),
    };
}

export function createAppReleaseCalendarRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/schedule`;
}

export function createAppTimelineRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/timeline`;
}

export function createAppTriageRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/triage`;
}

export function createBuildDistroBuildRoute(
    orgIdParam: string,
    appIdParam: string,
    buildId: string,
    bucketId?: string,
): string {
    const url = `${createAppBuildsRoute(orgIdParam, appIdParam)}?buildId=${buildId}`;

    if (bucketId) {
        return `${url}&bucketId=${bucketId}`;
    }

    return url;
}

export function createBuildDistroBucketBuildRoute(
    orgIdParam: string,
    appIdParam: string,
    bucketId: string,
    buildId: string,
): string {
    const url = createAppBuildDistroBucketRoute(orgIdParam, appIdParam, bucketId);

    return `${url}?buildId=${buildId}`;
}

export function createBuildDistroSharedWithMeBuildRoute(
    orgIdParam: string,
    appIdParam: string,
    buildId: string,
    bucketId?: string,
): string {
    const url = createAppSharedWithMeBucketRoute(orgIdParam, appIdParam);

    console.log('Showing...' + url);

    return `${url}?buildId=${buildId}&bucketId=${bucketId}`;
}

export function createAppBuildDistroBucketRoute(orgIdParam: string, appIdParam: string, bucketIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/bucket/${bucketIdParam}`;
}

export function createAppSharedWithMeBucketRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/shared-with-me`;
}

export function createExternalBuildDistroBucketBuildRoute(buildHashParam: string, bucketHashParam: string): string {
    return `/bucket/${bucketHashParam}?build=${buildHashParam}`;
}

export function createExternalBuildDistroBuildRoute(buildHashParam: string, bucketHashParam: string): string {
    return `/bucket/${bucketHashParam}/build/${buildHashParam}?build=${buildHashParam}`;
}

export function createAppOverviewRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/overview`;
}

export function createAppFeatureFlagsRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/feature-flags`;
}

export function createAppRolloutsRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/rollouts`;
}

export function createReleaseRoute(orgIdParam: string, appIdParam: string, releaseIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}`;
}

export function createSetupRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/setup`;
}

export function createSetupStepRoute(orgIdParam: string, appIdParam: string, setupTypeParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/setup/${setupTypeParam}`;
}

export function createReleaseStepRoute(
    orgIdParam: string,
    appIdParam: string,
    releaseIdParam: string,
    releaseStep: string,
): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/step/${releaseStep}`;
}

export const ROUTER_REDIRECT = 'ROUTER_REDIRECT';

export function createStepRunRoute(
    orgIdParam: string,
    appIdParam: string,
    releaseIdParam: string,
    stepRunId: string,
): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/step/${stepRunId}`;
}

export function createStepRunTabRoute(
    orgIdParam: string,
    workflowIdParam: string,
    workflowRunIdParam: string,
    stepRunId: string,
    tabType: ReleaseStepTabType,
): string {
    return `${createStepRunRoute(orgIdParam, workflowIdParam, workflowRunIdParam, stepRunId)}/${tabType}`;
}

export function createStepRunsSummaryRoute(
    orgIdParam: string,
    appIdParam: string,
    releaseIdParam: string,
    groupIndexParam: number,
    archetypeGroupIndexParam: number,
): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/stepRunsSummary/${groupIndexParam}/${archetypeGroupIndexParam}`;
}

export function createReleaseStepTabRoute(
    orgIdParam: string,
    appIdParam: string,
    releaseIdParam: string,
    releaseStep: string,
    tabType: ReleaseStepTabType,
): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/step/${releaseStep}/${tabType}`;
}

export function pickSearchParams(currentSearch: string, paramNames: string[]): string {
    const currentParams = new URLSearchParams(currentSearch);
    const pickedParams = new URLSearchParams();

    paramNames.forEach((paramName) => {
        const value = currentParams.get(paramName);
        if (value) {
            pickedParams.set(paramName, value);
        }
    });

    return pickedParams.toString();
}

export function createReleaseOverviewRoute(orgIdParam: string, appIdParam: string, releaseIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/overview`;
}

export function createReleaseRolloutRoute(
    orgIdParam: string,
    appIdParam: string,
    releaseIdParam: string,
    tabTypeParam = 'summary',
): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/rollout/${tabTypeParam}`;
}

export function createFeatureFlagsRoute(orgIdParam: string, appIdParam: string, releaseIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/featureFlags`;
}

export function createReleaseDependenciesRoute(orgIdParam: string, appIdParam: string, releaseIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/dependencies`;
}

export function createReleaseCalendarRoute(orgIdParam: string, appIdParam: string, releaseIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/schedule`;
}

export function createTimelineEventsRoute(orgIdParam: string, appIdParam: string, releaseIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/timeline`;
}

export function createReleaseTriageRoute(orgIdParam: string, appIdParam: string, releaseIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/releases/${releaseIdParam}/triage`;
}

export function createSettingsMenuRoute(orgIdParam: string, appIdParam: string, settingType: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/appSettings${settingType}`;
}

export function createAppSettingsIntegrationsRoute(orgIdParam: string, appIdParam: string): string {
    return `/dashboard/org/${orgIdParam}/app/${appIdParam}/appSettings/integrations`;
}

export function createAddTestDeviceRoute(orgIdParam: string, appIdParam: string): string {
    const appRoute = createAppRoute(orgIdParam, appIdParam);

    return `${appRoute}/add-test-device`;
}

export enum SettingsScrollTo {
    ChecklistItems = 'checklistItems',
    FeatureAffiliations = 'featureAffiliations',
    ReleaseBranchPatterns = 'releaseBranchPatterns',
    ReleaseTagPatterns = 'releaseTagPatterns',
    Automations = 'automations',
}

export const allRoutes = (routes: Record<string, string>) => {
    const allRoutes = [];

    forIn(routes, (value) => {
        allRoutes.push({ path: value });
    });

    return allRoutes;
};

export const routeForReleaseStepOrNav: (
    orgIdParam: string,
    appIdParam: string,
    releaseId: string,
    releaseStepOrNav: ReleaseStepType | ReleaseNav,
) => string = (
    orgIdParam: string,
    appIdParam: string,
    releaseId: string,
    releaseStepOrNav: ReleaseStepType | ReleaseNav,
) => {
    switch (releaseStepOrNav) {
        case ReleaseNav.Overview:
            return createReleaseOverviewRoute(orgIdParam, appIdParam, releaseId);
        case ReleaseNav.Rollouts:
            return createReleaseRolloutRoute(orgIdParam, appIdParam, releaseId);
        case ReleaseNav.FeatureFlags:
            return createFeatureFlagsRoute(orgIdParam, appIdParam, releaseId);
        default:
            return createReleaseStepTabRoute(
                orgIdParam,
                appIdParam,
                releaseId,
                releaseStepOrNav,
                ReleaseStepTabType.Summary,
            );
    }
};
